/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Button, Dropzone, Form, Icon, Modal } from '@mairistem/composants';

import MODAL_HEADER_STYLE from '@/components/Mention/Liste/modalStyles';

/**
 * Extensions acceptées, et le `dc_type` correspondant côté API.
 *
 * Les variantes d'une même famille partagent un type, comme le fait déjà la
 * base pour `tif`/`tiff`. `dc_type` est un `varchar(5)` : les valeurs doivent
 * rester courtes.
 */
const TYPES_PAR_EXTENSION: Record<string, string> = {
  tif: 'TIF',
  tiff: 'TIF',
  pdf: 'PDF',
  rtf: 'RTF',
  doc: 'DOC',
  docx: 'DOCX',
  txt: 'TXT',
  jpg: 'JPG',
  jpeg: 'JPG',
  png: 'PNG',
};

const OPTIONS_TYPE_ACTE = [
  { key: 'oui', value: true, text: 'Oui' },
  { key: 'non', value: false, text: 'Non' },
];

/**
 * Déduit le `dc_type` de l'extension du fichier.
 *
 * L'API valide ce champ contre une liste fermée
 * (`DOC|DOCX|RTF|TIF|TXT|PDF`) : un fichier d'une autre extension n'a pas de
 * type valide, et le formulaire le refuse.
 */
export const typeDepuisNomFichier = (nom?: string): string | undefined =>
  TYPES_PAR_EXTENSION[_.toLower(_.last(_.split(nom ?? '', '.')) ?? '')];

export interface NouveauDocument {
  nom: string;
  /** `dc_acte` : l'image de l'acte lui-même, ou une pièce annexe du dossier. */
  acte: boolean;
  description: string;
  fichier: File;
  /** `dc_type`, déduit de l'extension. */
  type: string;
}

interface ModalAjouterDocumentProps {
  open: boolean;
  /** Enregistrement en cours : désactive la validation. */
  saving?: boolean;
  onClose: () => void;
  onConfirm: (document: NouveauDocument) => void;
}

/**
 * Ajout d'un document au dossier d'un acte.
 *
 * Le type n'est pas demandé à l'utilisateur : il se déduit de l'extension du
 * fichier, seule source fiable — un nom saisi à la main ne dit rien du contenu.
 */
const ModalAjouterDocument = (props: ModalAjouterDocumentProps) => {
  const { open, saving = false, onClose, onConfirm } = props;

  const [nom, setNom] = React.useState('');
  const [acte, setActe] = React.useState<boolean | undefined>();
  const [description, setDescription] = React.useState('');
  const [fichier, setFichier] = React.useState<File | null>(null);

  // Repartir d'un formulaire vierge à chaque ouverture.
  React.useEffect(() => {
    if (open) return;
    setNom('');
    setActe(undefined);
    setDescription('');
    setFichier(null);
  }, [open]);

  const type = typeDepuisNomFichier(fichier?.name);

  const extensionRefusee = !_.isNil(fichier) && _.isNil(type);
  const valide =
    !_.isEmpty(_.trim(nom)) && !_.isNil(acte) && !_.isNil(type) && !saving;

  const handleDrop = React.useCallback((fichiers: File[]) => {
    if (!_.isEmpty(fichiers)) setFichier(fichiers[0]);
  }, []);

  const handleConfirm = React.useCallback(() => {
    if (!valide || !fichier || !type || _.isNil(acte)) return;

    onConfirm({
      nom: _.trim(nom),
      acte,
      description: _.trim(description),
      fichier,
      type,
    });
  }, [valide, fichier, type, acte, nom, description, onConfirm]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      {/* @ts-ignore */}
      <Modal.Header style={MODAL_HEADER_STYLE} content="Ajouter un document" />
      <Modal.Content>
        <Form>
          <Form.Group>
            <Form.Input
              required
              fluid
              label="Nom du document"
              placeholder="Nom du document"
              data-testid="document-nom"
              value={nom}
              width={12}
              onChange={(_e: unknown, data: { value: string }) =>
                setNom(data.value)
              }
            />
            <Form.Dropdown
              required
              fluid
              selection
              label="Document typé acte"
              placeholder="Oui / non"
              data-testid="document-type-acte"
              options={OPTIONS_TYPE_ACTE}
              value={acte}
              width={4}
              onChange={(_e: unknown, data: { value: boolean }) =>
                setActe(data.value)
              }
            />
          </Form.Group>
          <Form.TextArea
            label="Description"
            placeholder="Description du document (optionnel)"
            data-testid="document-description"
            rows={3}
            value={description}
            onChange={(_e: unknown, data: { value: string }) =>
              setDescription(data.value)
            }
          />
          <Form.Field required error={extensionRefusee}>
            <label>Sélectionner un fichier</label>
            <Dropzone
              style={{ cursor: 'pointer' }}
              accept=".pdf,.tif,.tiff,.jpg,.jpeg,.png,.rtf,.doc,.docx,.txt"
              data-testid="document-dropzone"
              // @ts-expect-error la signature de Dropzone n'expose pas onDrop
              onDrop={handleDrop}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#f0f0f0',
                  border: '1px dashed #ccc',
                  padding: '15px 20px',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              >
                <Icon name="FilePlus" iconSet="Lucide" size={24} />
                <span>Glissez/déposer un fichier ou cliquer</span>
              </div>
            </Dropzone>
            {fichier && !extensionRefusee && (
              <div
                data-testid="document-fichier-retenu"
                style={{
                  marginTop: '12px',
                  padding: '12px 16px',
                  backgroundColor: '#e8f5e9',
                  border: '1px solid #4caf50',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <Icon
                  name="FileCheck"
                  iconSet="Lucide"
                  size={20}
                  style={{ color: '#4caf50', flexShrink: 0 }}
                />
                <span
                  style={{
                    color: '#2e7d32',
                    fontWeight: 500,
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={fichier.name}
                >
                  {fichier.name}
                </span>
                <button
                  type="button"
                  data-testid="document-retirer-fichier"
                  onClick={() => setFichier(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#666',
                    flexShrink: 0,
                  }}
                  title="Retirer le fichier"
                >
                  <Icon name="X" iconSet="Lucide" size={18} />
                </button>
              </div>
            )}
            {extensionRefusee && (
              <div style={{ color: '#9f3a38', marginTop: '.5em' }}>
                Format non pris en charge. Formats acceptés : PDF, TIF, TIFF,
                JPG, JPEG, PNG, RTF, DOC, DOCX, TXT.
              </div>
            )}
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button data-testid="document-annuler" onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          data-testid="document-ajouter"
          loading={saving}
          disabled={!valide}
          onClick={handleConfirm}
        >
          <Icon name="add" />
          Ajouter
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalAjouterDocument;
